/** One session row in the review list: status/progress/token/cost/duration + direct preview. */
import { formatCost, formatDurationSeconds, formatRelative, formatTokens } from '../lib/format';
import { latestSessionEvent, sessionDurationSeconds, sessionProgress } from '../lib/sessions';
import type { SessionView } from '../lib/types';
import { EventKindBadge, SourceBadge, StatusBadge } from './SessionBadges';
import { SessionProgress } from './SessionProgress';
import { Icon } from './Icon';
import { MaskedText } from './MaskedText';

export function SessionCard({
  session,
  now,
  onOpen,
  subtaskCount = 0,
  expanded = false,
  onToggleSubtasks,
}: {
  session: SessionView;
  now: number;
  onOpen(session: SessionView): void;
  /** Number of descendants (all levels); >0 shows the subtask footer. */
  subtaskCount?: number;
  expanded?: boolean;
  onToggleSubtasks?(session: SessionView): void;
}) {
  const latest = latestSessionEvent(session);
  const duration = sessionDurationSeconds(session, now);
  const hasProgress = sessionProgress(session) !== null;
  const expandable = subtaskCount > 0 && onToggleSubtasks !== undefined;
  const activate = (): void => {
    if (expandable) onToggleSubtasks?.(session);
    else onOpen(session);
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={activate}
      onKeyDown={(event) => {
        if (event.target !== event.currentTarget) return;
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          activate();
        }
      }}
      aria-label={expandable ? '展开或收起分任务' : '查看会话详情'}
      aria-expanded={expandable ? expanded : undefined}
      className="w-full cursor-pointer rounded-2xl border border-white/10 bg-[#0f1629] p-3.5 text-left outline-none transition active:bg-white/[0.04] focus-visible:border-indigo-400/60"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-1.5">
            <SourceBadge source={session.source} />
            <StatusBadge status={session.status} />
            {session.model ? (
              <span className="rounded bg-black/30 px-1.5 py-0.5 text-[10px] text-slate-400">
                {session.model}
              </span>
            ) : null}
          </div>
          <div
            className="mt-1.5 text-sm text-slate-100"
            onClick={(event) => event.stopPropagation()}
          >
            <MaskedText value={session.title} />
          </div>
          {hasProgress ? <SessionProgress session={session} /> : null}
          {latest?.text || latest?.tool ? (
            <div
              className="mt-2 flex items-start gap-1.5 text-[11px] text-slate-400"
              onClick={(event) => event.stopPropagation()}
            >
              <EventKindBadge kind={latest.kind} />
              <div className="line-clamp-2 min-w-0 break-words">
                {latest.tool ? <code className="text-sky-200/80">{latest.tool} · </code> : null}
                {latest.text ? <MaskedText value={latest.text} multiline /> : null}
              </div>
            </div>
          ) : null}
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1.5">
          <Icon name="chevron" className="h-4 w-4 text-slate-600" />
          <span className="whitespace-nowrap text-[10px] text-slate-500">
            {formatRelative(session.last_event_at, now)}
          </span>
        </div>
      </div>
      <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[10px] text-slate-500">
        <span>token {formatTokens(session.tokens)}</span>
        <span>费用 {formatCost(session.cost_cny)}</span>
        <span>耗时 {formatDurationSeconds(duration)}</span>
        <span>事件 {session.events.length} 条</span>
      </div>
      {expandable ? (
        <div
          className="mt-2 flex items-center justify-between gap-2 border-t border-white/5 pt-2"
          onClick={(event) => event.stopPropagation()}
          onKeyDown={(event) => event.stopPropagation()}
        >
          <span className="flex items-center gap-1 text-[11px] text-indigo-200">
            <Icon
              name="chevron"
              className={`h-3.5 w-3.5 transition-transform ${expanded ? 'rotate-90' : ''}`}
            />
            {subtaskCount} 个分任务{expanded ? '（已展开）' : ''}
          </span>
          <span className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={() => onToggleSubtasks?.(session)}
              className="rounded-lg border border-indigo-400/30 bg-indigo-400/10 px-2 py-1 text-[11px] text-indigo-100"
            >
              {expanded ? '收起' : '展开'}
            </button>
            <button
              type="button"
              onClick={() => onOpen(session)}
              className="rounded-lg border border-white/10 bg-white/5 px-2 py-1 text-[11px] text-slate-300"
            >
              详情
            </button>
          </span>
        </div>
      ) : null}
    </div>
  );
}
